import React, { useState } from 'react';
import './TaskConfirmation.css';

export interface TaskIdentification {
  task_type: string | null;
  domain: string | null;
  confidence: 'high' | 'medium' | 'low' | 'none';
  confidence_score: number;
  reasoning: string;
  matched_patterns: string[];
  alternative_tasks: string[];
  requires_confirmation: boolean;
  confirmation_prompt: string;
}

interface TaskConfirmationProps {
  taskIdentification: TaskIdentification;
  onConfirm: (confirmed: boolean, selectedTask?: string) => void;
  onTaskChange: (newTask: string) => void;
}

const TaskConfirmation: React.FC<TaskConfirmationProps> = ({
  taskIdentification,
  onConfirm,
  onTaskChange
}) => {
  const [showAlternatives, setShowAlternatives] = useState(false);
  const [selectedTask, setSelectedTask] = useState(taskIdentification.task_type);
  const [customTask, setCustomTask] = useState('');

  const formatTaskName = (taskType: string | null): string => {
    if (!taskType) return 'General Analysis';
    
    const displayNames: Record<string, string> = {
      'bank_reconciliation': 'Bank Reconciliation',
      'ar_aging': 'Accounts Receivable Aging',
      'ap_aging': 'Accounts Payable Aging',
      'journal_entry': 'Journal Entry',
      'revenue_recognition': 'Revenue Recognition',
      'expense_categorization': 'Expense Categorization',
      'financial_consolidation': 'Financial Consolidation',
      'cash_flow_analysis': 'Cash Flow Analysis',
      'variance_analysis': 'Variance Analysis',
      'budget_comparison': 'Budget vs Actual Comparison'
    };
    
    return displayNames[taskType] || taskType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getConfidenceColor = (confidence: string): string => {
    switch (confidence) {
      case 'high': return 'task-confidence-high';
      case 'medium': return 'task-confidence-medium';
      case 'low': return 'task-confidence-low';
      default: return 'task-confidence-none';
    }
  };

  const getConfidenceEmoji = (confidence: string): string => {
    switch (confidence) {
      case 'high': return '✅';
      case 'medium': return '🤔';
      case 'low': return '❓';
      default: return 'ℹ️';
    }
  };

  const handleConfirm = () => {
    onConfirm(true, selectedTask || undefined);
  };

  const handleReject = () => {
    setShowAlternatives(true);
  };

  const handleSelectAlternative = (task: string) => {
    setSelectedTask(task);
    onTaskChange(task);
    onConfirm(true, task);
  };

  const handleNotSure = () => {
    onConfirm(false);
  };

  if (!taskIdentification.requires_confirmation && taskIdentification.confidence === 'high') {
    // Auto-confirmed, just show info
    return (
      <div className="task-confirmation-info">
        <div className={`task-badge ${getConfidenceColor(taskIdentification.confidence)}`}>
          {getConfidenceEmoji(taskIdentification.confidence)} {formatTaskName(taskIdentification.task_type)}
        </div>
        <div className="task-reasoning">{taskIdentification.reasoning}</div>
      </div>
    );
  }

  return (
    <div className="task-confirmation-container">
      <div className="task-confirmation-header">
        <h3>Task Identification</h3>
        <div className={`confidence-indicator ${getConfidenceColor(taskIdentification.confidence)}`}>
          {getConfidenceEmoji(taskIdentification.confidence)} Confidence: {taskIdentification.confidence}
        </div>
      </div>

      <div className="task-confirmation-body">
        <div className="task-identification-display">
          <p className="task-prompt">{taskIdentification.confirmation_prompt}</p>
          
          {taskIdentification.task_type && (
            <div className="identified-task">
              <strong>Identified as:</strong> {formatTaskName(taskIdentification.task_type)}
            </div>
          )}
          
          {taskIdentification.matched_patterns.length > 0 && (
            <div className="matched-patterns">
              <strong>Patterns found:</strong>
              <ul>
                {taskIdentification.matched_patterns.slice(0, 3).map((pattern, idx) => (
                  <li key={idx}>{pattern}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {!showAlternatives ? (
          <div className="task-confirmation-actions">
            <button 
              className="btn btn-primary"
              onClick={handleConfirm}
            >
              ✓ Correct
            </button>
            <button 
              className="btn btn-secondary"
              onClick={handleReject}
            >
              ✗ Change Task Type
            </button>
            <button 
              className="btn btn-tertiary"
              onClick={handleNotSure}
            >
              ? Not Sure - Continue Without Framework
            </button>
          </div>
        ) : (
          <div className="task-alternatives">
            <h4>Select the correct task type:</h4>
            <div className="task-options">
              {taskIdentification.alternative_tasks.map((task) => (
                <button
                  key={task}
                  className={`task-option ${selectedTask === task ? 'selected' : ''}`}
                  onClick={() => handleSelectAlternative(task)}
                >
                  {formatTaskName(task)}
                </button>
              ))}
              <button
                className="task-option general"
                onClick={handleNotSure}
              >
                General Analysis (No specific task)
              </button>
            </div>
            
            <div className="custom-task-input">
              <label htmlFor="custom-task">Or enter a custom task type:</label>
              <div className="custom-task-row">
                <input
                  id="custom-task"
                  type="text"
                  value={customTask}
                  onChange={(e) => setCustomTask(e.target.value)}
                  placeholder="e.g., Depreciation Schedule"
                />
                <button
                  className="btn btn-primary"
                  onClick={() => handleSelectAlternative(customTask)}
                  disabled={!customTask.trim()}
                >
                  Use Custom
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {taskIdentification.domain && (
        <div className="task-domain-info">
          <small>Domain: {taskIdentification.domain}</small>
        </div>
      )}
    </div>
  );
};

export default TaskConfirmation;